<script lang="ts" setup>
// 📌 Macro
defineExpose({
  openModal
});
// 📌 Store
const tokenStore = useTokenStore();
const userConfiguration = useUserConfigurationStore();
const LoginApi = useLoginAPI();
const roleManagerStore = useRoleManagerStore();
// 📌 Utils
const biometric = useBiometric();
const { t } = useEkI18n();
const { openConfirmAlert } = useUserMessages();
const { registerEnterKey, unregisterEnterKey } = useWebInputManager();

// 📌 Refs
const isEdit = ref<boolean>(false);
const showPasswordModal = ref<boolean>(false);
const showPasswordInput = ref<boolean>(false);
const isSiteSelectOpen = ref<boolean>(false);
const isPasswordTouched = ref<boolean>(false);
const password = ref<string>('');
const selectedAccount = ref<StoredAccount | null>(null);
const localAccounts = ref<StoredAccount[]>([]);
const passwordInputRef = useTemplateRef('PasswordInputRef');
// 📌 Computed Properties
const currentSite = computed(() => {
  const site = userConfiguration._userConfiguration?.SiteName.toLowerCase() ?? '';

  return site.replace(/^ekomplet_/, ''); // removes leading "ekomplet_"
});
const currentUser = computed(() => userConfiguration._userConfiguration?.UserName.toLowerCase());

// 📌 Functions
async function openModal() {
  isSiteSelectOpen.value = true;
  await biometric.checkAvailability();
  await tokenStore.getAllAccounts();
}
 
function close() {
  isEdit.value = false;
  localAccounts.value = [];
  isSiteSelectOpen.value = false;
}

async function toggleEdit() {
  if (!isEdit.value) {
    localAccounts.value = JSON.parse(JSON.stringify(tokenStore.storedAccount));
  }

  if (isEdit.value) {
    const confirmed = await openConfirmAlert(
      t('buttons.confirm'),
      t('alert.editSiteChange'),
      t('buttons.yes'),
      t('buttons.no')
    );

    if (!confirmed) {
      return;
    }
  }

  isEdit.value = !isEdit.value;
}

function deleteAccount(account: StoredAccount) {
  localAccounts.value = localAccounts.value.filter(
    a => !(a.username === account.username && a.siteName === account.siteName)
  );
}

function doReorder(event: CustomEvent) {
  const from = event.detail.from;
  const to = event.detail.to;

  const moved = localAccounts.value.splice(from, 1)[0];
  localAccounts.value.splice(to, 0, moved);

  event.detail.complete();
}

async function saveOrder() {
  if (localAccounts.value.length > 0) {
    await tokenStore.saveAccountsOrder(localAccounts.value);
    await tokenStore.getAllAccounts();
    isEdit.value = false;
  }
}

async function cancelEdit() {
  const confirmed = await openConfirmAlert(
    t('buttons.confirm'),
    t('alert.editSiteChange'),
    t('buttons.yes'),
    t('buttons.no')
  );

  if (confirmed) {
    isEdit.value = false;
    localAccounts.value = []; 
  }

}

async function verify(account: StoredAccount): Promise<string | null> {
  if (biometric.isAvailable.value) {
    const isVerified = await biometric.verifyBiometricIdentity();

    if (isVerified) {
      const passwordKey = await tokenStore.getPasswordKeyForAccount(account.username, account.siteName);

      if (!passwordKey) return null;

      return tokenStore.decryptPassword(account.password, passwordKey);
    } else {
      // User declined biometric → open modal
      password.value = '';
      showPasswordModal.value = true;

      return null;
    }
  } else {
    // No biometric → open modal
    password.value = '';
    showPasswordModal.value = true;

    return null;
  }
}

async function login(account: StoredAccount, pwd: string) {
  await showLoading(t('login.toast.loggingIn'));
  useTokenStore().$reset();

  const loginRequestBody = await loginHelpers.createLoginRequestBody(account.username, pwd, account.siteName);
  const fetchResponse = await LoginApi.authenticate(loginRequestBody);

  if (!fetchResponse?.Data || fetchResponse?.HasErrors === true) {
    await hideLoading();

    return;
  }

  const promises = [
    tokenStore.setRefreshToken(fetchResponse.Data.Tokens.RefreshToken, fetchResponse.Data.RefreshTokenExp),
    tokenStore.setAccessToken(fetchResponse.Data.Tokens.AccessToken, fetchResponse.Data.AccessTokenExp)
  ];
  await Promise.all(promises);

  await loginHelpers.fetchConfigurationData();
  const userConfiguration = useUserConfigurationStore();

  if (!userConfiguration._userConfiguration) {
    await hideLoading();

    return;
  }

  location.replace(roleManagerStore.loginDestination);
  await hideLoading();
}

async function confirmPasswordFromModal() {
  if (!password.value || !selectedAccount.value) return;
  await login(selectedAccount.value, password.value);
}

async function changeSite(account: StoredAccount) {
  selectedAccount.value = account;
  const pwd = await verify(account);

  if (!pwd) {
    return;
  }

  await login(account, pwd);
}

function onOpenPasswordDialog() {
  registerEnterKey(confirmPasswordFromModal);
  passwordInputRef.value?.$el.setFocus();
}

function onDismissPasswordDialog() {
  unregisterEnterKey();
  showPasswordModal.value = false;
}
</script>

<template>
  <IonModal
    :is-open="isSiteSelectOpen"
    :title="t('login.site')"
    @did-dismiss="isSiteSelectOpen = false"
  >
    <IonHeader class="ion-no-border">
      <IonToolbar>
        <IonTitle>{{ t('login.site') }}</IonTitle>
        <IonButtons slot="start">
          <IonButton
            tabindex="-1"
            @click="close"
          >
            <IonIcon
              slot="icon-only"
              class="fa-light fa-xmark"
            />
          </IonButton>
        </IonButtons>
        <IonButtons slot="end">
          <IonButton
            v-tooltip.top="t('tooltip.editSite')"
            tabindex="-1"
            @click="toggleEdit"
          >
            <IonIcon
              slot="icon-only"
              class="fa-fw fa-light fa-edit"
            />
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <div v-if="!isEdit">
        <IonList>
          <IonItem
            v-for="account in tokenStore.storedAccount"
            :key="account.siteName + '_' + account.username"
            button
            :disabled="account.siteName.toLowerCase() === currentSite.toLowerCase() && account.username.toLowerCase() === currentUser"
            @click="changeSite(account)"
          >
            <IonLabel>
              <h2 class="capitalize">
                {{ account.siteName }}
              </h2>
              <IonNote class="capitalize">
                {{ account.username }}
              </IonNote>
            </IonLabel>
            <CheckedText
              v-if="account.siteName.toLowerCase() === currentSite.toLowerCase() && account.username.toLowerCase() === currentUser"
              label=""
              :checked="account.siteName.toLowerCase() === currentSite.toLowerCase() && account.username.toLowerCase() === currentUser"
            />
          </IonItem>
        </IonList>
        <IonNote>
          <p class="ion-padding">
            {{ t('login.toast.siteSelect') }}
          </p>
        </IonNote>
      </div>
      <div v-else>
        <IonReorderGroup
          :disabled="false"
          @ion-item-reorder="doReorder($event)"
        >
          <IonItem
            v-for="account in localAccounts"
            :key="account.siteName + '_' + account.username"
            button
            lines="full"
          >
            <IonLabel>
              <h2>{{ account.siteName.charAt(0).toUpperCase() + account.siteName.slice(1) }}</h2>
              <IonNote>
                {{ account.username.charAt(0).toUpperCase() + account.username.slice(1) }}
              </IonNote>
            </IonLabel>
            <IonReorder slot="start" />
            <IonButton
              v-if="!(account.siteName === currentSite && account.username === currentUser)"
              fill="clear"
              @click="deleteAccount(account)"
            >
              <IonIcon class="fa-light fa-trash-can" />
            </IonButton>
          </IonItem>
        </IonReorderGroup>
        <IonNote>
          <p class="ion-padding">
            {{ t('login.toast.siteSelectReorder') }}
          </p>
        </IonNote>
      </div>
    </IonContent>
    <IonFooter v-if="isEdit">
      <IonRow>
        <IonCol>
          <ButtonsRegular
            :button-text="t('buttons.cancel')"
            type="cancel"
            @click="cancelEdit"
          />
        </IonCol>
        <IonCol>
          <ButtonsRegular
            :button-text="t('buttons.save')"
            type="ok"
            @click="saveOrder"
          />
        </IonCol>
      </IonRow>
    </IonFooter>
    <IonModal
      class="ion-padding custom-ion-modal"
      :is-open="showPasswordModal"
      @did-dismiss="onDismissPasswordDialog"
      @did-present="onOpenPasswordDialog"
    >
      <IonToolbar>
        <IonTitle class="ion-no-padding">
          <div class="ion-text-wrap ion-padding">
            {{ t('login.password') }}
          </div>
        </IonTitle>
        <IonButtons slot="end">
          <IonButton @click="() => showPasswordModal = false">
            <IonIcon class="fa-light fa-xmark" />
          </IonButton>
        </IonButtons>
      </IonToolbar>
      <IonNote class="ion-text-wrap ion-padding">
        {{ t('login.usePasswordNote') }}
      </IonNote>
      <IonList>
        <IonItem
          lines="full"
          :class="{'ion-touched': isPasswordTouched}"
        >
          <IonIcon
            slot="start"
            class="fa-light fa-fw fa-user"
          />
          <IonInput
            ref="PasswordInputRef"
            v-model="password"
            :type="showPasswordInput ? 'text' : 'password'"
            :required="true"
            label-placement="floating"
            :label="t('login.password')"
            @ion-blur="isPasswordTouched = true"
          />
          <IonButton
            slot="end"
            fill="default"
            color="bar"
            class="showPassword"
            @click="showPasswordInput = !showPasswordInput"
          >
            <IonIcon
              slot="icon-only"
              class="fa-solid fa-fw"
              :class="showPasswordInput ? 'fa-eye-slash' : 'fa-eye'"
            />
          </IonButton>
        </IonItem>
      </IonList>
      <IonRow class="sm-padding">
        <IonCol>
          <IonButton
            expand="block"
            shape="round"
            fill="outline"
            @click="() => showPasswordModal = false"
          >
            {{ t('buttons.cancel') }}
          </IonButton>
        </IonCol>
        <IonCol>
          <IonButton
            expand="block"
            shape="round"
            @click="confirmPasswordFromModal"
          >
            {{ t('login.login') }}
          </IonButton>
        </IonCol>
      </IonRow>
    </IonModal>
  </IonModal>
</template>

<style scoped>
.custom-ion-modal {
  --height: auto;
  --border-radius: 16px;
}
.sm-padding {
  padding: 8px;
}
.capitalize {
  text-transform: capitalize;
}
</style>
